import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DisclosureSetOption } from '../model/disclosure-settings.interface';

export interface DisclosureSetSelectOption {
  label: string;
  value: number;
}

export interface DisclosureSetSource {
  disclosureSets: DisclosureSetOption[];
  enhancedDisclosure: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DisclosureSetOptionsService
{
  private static readonly STANDARD_PLUS_DOC_FEE_DISCLOSURE_SET_ID = 3;
  private static readonly HONDA_OEM_DISCLOSURE_SET_ID = 4;
  private static readonly MERCEDES_BENZ_OEM_DISCLOSURE_SET_NAME = 'Mercedes-Benz OEM';

  private static readonly STANDARD_PLUS_DOC_FEE_TOOLTIP =
    'Includes the Doc Fee from Ad Types > Fees in the disclosure, even if set to $0.';
  private static readonly HONDA_OEM_TOOLTIP =
    'Removes any "rebate" terminology from the disclosure.';

  private static readonly DISCLOSURE_SET_TOOLTIPS: Record<number, string> = {
    [DisclosureSetOptionsService.STANDARD_PLUS_DOC_FEE_DISCLOSURE_SET_ID]: DisclosureSetOptionsService.STANDARD_PLUS_DOC_FEE_TOOLTIP,
    [DisclosureSetOptionsService.HONDA_OEM_DISCLOSURE_SET_ID]: DisclosureSetOptionsService.HONDA_OEM_TOOLTIP,
  };

  /**
   * Filters and maps the raw disclosure sets from the source into labelled dropdown options,
   * excluding sets that are not allowed for the current enhancedDisclosure state.
   */
  buildDisclosureSetOptions(disclosureSetSource: DisclosureSetSource): DisclosureSetSelectOption[]
  {
    let options: DisclosureSetSelectOption[] = [];
    const sets = disclosureSetSource?.disclosureSets;

    if (Array.isArray(sets)) {
      const enhancedEnabled = !!disclosureSetSource?.enhancedDisclosure;
      options = sets
        .filter((disclosureSet: DisclosureSetOption) => disclosureSet && Number.isFinite(disclosureSet.id))
        .filter((disclosureSet: DisclosureSetOption) => this.isDisclosureSetAllowed(disclosureSet, enhancedEnabled))
        .map((disclosureSet: DisclosureSetOption) => this.buildDisclosureSetOption(disclosureSet));
    }

    return options;
  }

  /**
   * Computes the tooltip for the currently selected disclosure set and writes
   * it back to the provided target object via the supplied setter.
   */
  updateDisclosureSetTooltip(
    form: UntypedFormGroup,
    disclosureSetOptions: DisclosureSetSelectOption[],
    setTooltip: (tooltip: string) => void
  ): void
  {
    const selectedValue = form.get('disclosureSet')?.value;
    const selectedOption = disclosureSetOptions.find(option => option.value === selectedValue);

    const tooltipMap = DisclosureSetOptionsService.DISCLOSURE_SET_TOOLTIPS;
    let tooltip = '';

    if (selectedOption !== undefined && selectedOption.value in tooltipMap) {
      tooltip = tooltipMap[selectedOption.value];
    }

    setTooltip(tooltip);
  }

  /**
   * Returns false for the Mercedes-Benz OEM set when enhanced disclosure is enabled,
   * as that combination is not supported.
   */
  private isDisclosureSetAllowed(disclosureSet: DisclosureSetOption, enhancedEnabled: boolean): boolean
  {
    let allowed = true;

    if (enhancedEnabled) {
      allowed = disclosureSet?.name !== DisclosureSetOptionsService.MERCEDES_BENZ_OEM_DISCLOSURE_SET_NAME;
    }

    return allowed;
  }

  /**
   * Maps a raw DisclosureSetOption to a labelled select option, using the set name when available
   * and falling back to the numeric ID as a string.
   */
  private buildDisclosureSetOption(disclosureSet: DisclosureSetOption): DisclosureSetSelectOption
  {
    let label = String(disclosureSet.id);

    if (disclosureSet?.name?.length) {
      label = disclosureSet.name;
    }

    return { label, value: Number(disclosureSet.id) };
  }
}
